import useSWR from "swr";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Package, Users, Tag, MessageCircle, Eye } from "lucide-react";
import { fetcher, brl, formatDate } from "../../lib/api";
import { PageHeader, Panel } from "../../components/admin/AdminUI";

const Kpi = ({ label, value, sub, Icon, testid }) => (
  <div className="rounded-2xl border border-[#D4AF37]/12 bg-[#141417] p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA]">{label}</p>
      <Icon className="h-4 w-4 text-[#D4AF37]" />
    </div>
    <p className="mt-3 font-serif text-4xl text-[#FAF6F0]" data-testid={`${testid}-value`}>{value ?? "—"}</p>
    {sub && <p className="mt-1 text-xs text-[#A1A1AA]">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const { data: d } = useSWR("/admin/dashboard", fetcher, { refreshInterval: 30000 });
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Visão geral da sua loja" />
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
        <Kpi label="Produtos" value={d?.products_total} sub={d && `${d.products_active} ativos`} Icon={Package} testid="kpi-products" />
        <Kpi label="Clientes" value={d?.customers_total} sub={d && `${d.customers_consent} aceitam ofertas`} Icon={Users} testid="kpi-customers" />
        <Kpi label="Promoções ativas" value={d?.promotions_active} Icon={Tag} testid="kpi-promotions" />
        <Kpi label="Solicitações" value={d?.requests_total} sub="cliques no WhatsApp" Icon={MessageCircle} testid="kpi-requests" />
        <Kpi label="Acessos hoje" value={d?.visits_today} sub={d && `${d.visits_14d} nos últimos 14 dias`} Icon={Eye} testid="kpi-visits" />
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Panel title="Acessos e solicitações (14 dias)" className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.chart || []}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#25D366" stopOpacity={0.35} /><stop offset="100%" stopColor="#25D366" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                <Tooltip contentStyle={{ background: "#1C1C21", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12 }} />
                <Area type="monotone" dataKey="visitas" name="Acessos" stroke="#D4AF37" fill="url(#g1)" strokeWidth={2} />
                <Area type="monotone" dataKey="solicitacoes" name="Solicitações" stroke="#25D366" fill="url(#g2)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Mais solicitados">
          {d?.top_products?.length ? (
            <ol className="space-y-3">
              {d.top_products.map((p, i) => (
                <li key={p.id} className="flex items-center gap-3 text-sm"><span className="font-serif text-lg text-[#D4AF37]">{i + 1}</span><span className="flex-1 truncate">{p.name}<span className="block text-xs text-[#A1A1AA]">{p.brand}</span></span><span className="text-[#A1A1AA]">{p.request_count}</span></li>
              ))}
            </ol>
          ) : <p className="text-sm text-[#A1A1AA]">Ainda sem solicitações registradas.</p>}
        </Panel>
      </div>
      <Panel title="Solicitações recentes" className="mt-6" testid="recent-requests">
        {d?.recent_requests?.length ? (
          <div className="divide-y divide-white/5">
            {d.recent_requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-sm">
                <span className="w-32 text-[#A1A1AA]">{formatDate(r.created_at, true)}</span>
                <span className="w-36">{r.phone || "Visitante"}</span>
                <span className="flex-1 truncate text-[#C9C4BA]">{r.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</span>
                <span className="text-[#F3E5AB]">{brl(r.total)}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#A1A1AA]">Nenhuma solicitação ainda. Elas aparecem quando clientes clicam em “Comprar pelo WhatsApp”.</p>}
      </Panel>
    </div>
  );
}
